import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  TextField,
  Paper,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';
import { useParams, useNavigate } from "react-router-dom";
import FeedbackHeading from "./FeedbackHeading";
import useStyles from "../../assets/styles/style";
import SnackbarNotification from "../snackbar/snackbarNotification";
import { AppDispatch, RootState } from "../../redux/store/appStore";
import { useDispatch, useSelector } from "react-redux";
import { getReview } from "../../redux/slices/reviewerSlice";
import { STATUS } from "../../utils/status";
import { createFeedback, resetCreateStatus, resetUpdateStatus, singleFeedback, updateFeedback } from "../../redux/slices/feedback/feedbackSlice";
import { fetchVendorsAction } from "../../redux/slices/vendor";
import RichTextEditor from '../project/textEditor';

const RECIPIENT_OPTIONS = [
  { value: "ALL_EXPERTS", label: "All Experts" },
  { value: "ALL_TASKERS", label: "All Taskers" },
  { value: "ALL_REVIEWERS", label: "All Reviewers" },
  { value: "SPECIFIC_VENDOR", label: "All users from a specific Vendor" },
  { value: "QC_PASSED_TASKERS", label: "All QC passed taskers" },
  { value: "QC_FAILED_TASKERS", label: "All QC failed taskers" },
  { value: "QC_PENDING_TASKERS", label: "All QC pending taskers" },
];

const CreateFeedbackPage = () => {
  const navigate = useNavigate();
  const { id, feedbackId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const originalDataRef = useRef<any>(null);
  const dataLoadedRef = useRef<boolean>(false);
  const initializingRef = useRef<boolean>(false);

  const feedbackRow = useSelector((state: RootState) => state.feedback.feedbackRow as any);
  const [selectedStages, setSelectedStages] = useState<number[]>([]);
  const [recipient, setRecipient] = useState<string>("");
  const [vendorId, setVendorId] = useState<any>("");
  const [feedbackText, setFeedbackText] = useState<string>("");
  const [longDescription, setLongDescription] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [noVendorsFound, setNoVendorsFound] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(feedbackId ? true : false);

  const getReviewStages = useSelector((state: RootState) => state.reviewStage.reviewer);
  const singleFeedbackStatus = useSelector((state: RootState) => state.feedback.singleFeedbackStatus);
  const getReviewStatus = useSelector((state: RootState) => state.reviewStage.getReviewStatus);
  const vendorsList = useSelector((state: RootState) => state.vendors.ListOnBoardVendors)
  const createFeedbackStatus = useSelector((state: RootState) => state.feedback.createFeedbackStatus as any);
  const createFeedbackMessage = useSelector((state: RootState) => state.feedback.createFeedbackMessage as any);
  const updateFeedbackStatus = useSelector((state: RootState) => state.feedback.updateFeedbackStatus as any);
  const updateFeedbackMessage = useSelector((state: RootState) => state.feedback.updateFeedbackMessage as any);

  useEffect(() => {
    dispatch(fetchVendorsAction({ project_id: id }));
    dispatch(getReview({ project_id: id || "" }));
  }, []);

  useEffect(() => {
    if (feedbackId) {
      setIsLoading(true);
      initializingRef.current = false;
      dispatch(singleFeedback(feedbackId));
    }
  }, [feedbackId]);

  useEffect(() => {
    if (feedbackId && singleFeedbackStatus === STATUS.SUCESS && feedbackRow && !dataLoadedRef.current) {
      initializingRef.current = true;
      setIsLoading(false);
      const stageIds = (feedbackRow?.assigned_stages || []).map((s: any) => s.id);
      const description = feedbackRow.description || "";

      setSelectedStages(stageIds);
      setRecipient(feedbackRow?.recipient_type || "");
      setVendorId(feedbackRow?.vendor || "");
      setFeedbackText(feedbackRow?.title || "");
      setLongDescription(description);

      originalDataRef.current = {
        stages: stageIds,
        recipient: feedbackRow?.recipient_type || "",
        vendor: feedbackRow?.vendor || null,
        title: feedbackRow?.title || "",
        description: description,
      };
    }
  }, [feedbackId, singleFeedbackStatus, feedbackRow]);

  const getUpdatedFields = () => {
    if (!originalDataRef.current) return {};
    const original = originalDataRef.current;
    const updated: any = {};
    
    if (JSON.stringify([...original.stages].sort((a, b) => a - b)) !== JSON.stringify([...selectedStages].sort((a, b) => a - b))) {
      updated.stage_ids = selectedStages;
    }

    if (original.recipient !== recipient) {
      updated.recipient_type = recipient;
    }

    if ((original.vendor || "") !== (vendorId || "")) {
      updated.vendor = vendorId || null;
    }

    if (original.title !== feedbackText) {
      updated.title = feedbackText;
    }

    const originalDesc = original.description || "";
    const currentDesc = longDescription || "";

    if (originalDesc !== currentDesc) {
      updated.description = longDescription;
    }

    return updated;
  };


  const errors = {
    stages: formSubmitted && selectedStages.length === 0,
    recipient: formSubmitted && recipient === "",
    feedback: formSubmitted && feedbackText.trim() === "",
  };

  const classes = useStyles();

  const handleSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDescriptionChange = (newContentHTML: { json: string; html: string }) => {
    setLongDescription(newContentHTML.html);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const newErrors = {
      stages: selectedStages.length === 0,
      recipient: recipient === "",
      feedback: feedbackText.trim() === "",
    };

    setFormSubmitted(true);

    if (newErrors.stages || newErrors.recipient || newErrors.feedback) {
      handleSnackbar(`Please fill all required fields.`, "error");
      return;
    }

    if (feedbackId) {
      const editPayload = getUpdatedFields();

      if (Object.keys(editPayload).length === 0) {
        handleSnackbar("No changes detected to update.", "info");
        return;
      }

      dispatch(updateFeedback({ id: feedbackId, editPayload }));
    } else {
      const payload = {
        sub_project: id || "",
        title: feedbackText,
        recipient_type: recipient,
        is_enabled: false,
        ...(longDescription ? { description: longDescription } : {}),
        ...(recipient === "SPECIFIC_VENDOR" && vendorId ? { vendor: vendorId } : {}),
        stage_ids: selectedStages,
      };

      dispatch(createFeedback(payload));
    }
  };


  useEffect(() => {
    if (vendorsList?.results?.results?.length === 0) {
      setNoVendorsFound(true);
    } else {
      setNoVendorsFound(false);
    }
  }, [vendorsList]);

  const handleRecipientChange = (e: any) => {
    const value = e.target.value;
    if (value === "SPECIFIC_VENDOR") {
      if (noVendorsFound) {
        handleSnackbar("No vendors found for this project.", "error");
        return;
      }
    }
    setRecipient(value);
  };

  useEffect(() => {
    const handleUpdateFeedbackStatus = () => {
      if (createFeedbackStatus === STATUS.SUCESS) {
        handleSnackbar(createFeedbackMessage, 'success')
        dispatch(resetCreateStatus());
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      }
      if (createFeedbackStatus === STATUS.FAILED) {
        handleSnackbar(createFeedbackMessage, 'error');
        dispatch(resetCreateStatus());
      }
    }
    handleUpdateFeedbackStatus();
  }, [createFeedbackStatus]);

  useEffect(() => {
    const handleUpdateFeedbackStatus = () => {
      if (updateFeedbackStatus === STATUS.SUCESS) {
        handleSnackbar(updateFeedbackMessage, 'success')
        dispatch(resetUpdateStatus());
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      }
      if (updateFeedbackStatus === STATUS.FAILED) {
        handleSnackbar(updateFeedbackMessage, 'error');
        dispatch(resetUpdateStatus());
      }
    }
    handleUpdateFeedbackStatus();
  }, [updateFeedbackStatus]);

  if (isLoading && feedbackId) {
    return (
      <Box className={classes.formMainContainer}>
        <Box className={classes.formMainHeadingBox}>
          <FeedbackHeading />
        </Box>
        <Box component="main" className={classes.formMainBox}>
          <Paper className={classes.formMainpaper} sx={{ padding: 4 }}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <Typography> <CircularProgress /> Loading feedback...</Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.formMainContainer}>
      <Box className={classes.formMainHeadingBox}>
        <FeedbackHeading />
      </Box>

      <Box component="main" className={classes.formMainBox}>
        <Paper className={classes.formMainpaper} sx={{ padding: 4 }}>
          <Box component="form" className={classes.formContainer}>
            <Typography variant="h5" fontFamily="montserrat" mb={5}>
              {feedbackRow?.id ? "Update Feedback" : "Create Feedback"}
            </Typography>

            {/* Choose Stage */}
            <Box sx={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
              <Box sx={{ width: "400px" }}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    color: "#10383D",
                    marginTop: "5px",
                  }}
                >
                  Choose Stage
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "#10383D",
                  }}
                >
                  Select the stages for this feedback
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth error={errors.stages} size="small">
                  <InputLabel id="stage-multi-label">Choose Stage</InputLabel>
                  <Select
                    labelId="stage-multi-label"
                    multiple
                    value={selectedStages}
                    onChange={(e) => setSelectedStages(e.target.value as number[])}
                    input={<OutlinedInput label="Choose Stage" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const stage = getReviewStages?.results?.find(
                            (item: any) => item.id === value
                          );

                          return (
                            <Chip
                              key={value}
                              label={stage?.name}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {getReviewStatus === STATUS.SUCESS &&
                      getReviewStages?.results?.map((option: any) => {
                        return (
                          <MenuItem key={option.id} value={option.id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                minWidth: "200px",
                              }}
                            >
                              <Tooltip title={option.name} arrow>
                                <span
                                  style={{
                                    fontFamily: "montserrat",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    maxWidth: "150px",
                                    display: "inline-block",
                                  }}
                                >
                                  {option.name}
                                </span>
                              </Tooltip>

                              <Chip
                                size="small"
                                sx={{
                                  fontSize: "11px",
                                  backgroundColor: option.category === "REVIEW" ? "#1976d2" : "default",
                                  color: option.category === "REVIEW" ? "#ffffff" : "#000000",
                                  border: "1px solid #ccc",
                                  marginLeft: "10px",
                                  flexShrink: 0,
                                }}
                                label={option.category || "Task"}
                              />
                            </Box>
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Choose Recipient */}
            <Box sx={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
              <Box sx={{ width: "400px" }}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    color: "#10383D",
                    marginTop: "5px",
                  }}
                >
                  Choose Recipient
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "#10383D",
                  }}
                >
                  Select who will receive this feedback
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth error={errors.recipient} size="small">
                  <InputLabel id="recipient-single-label">Choose Recipient</InputLabel>
                  <Select
                    labelId="recipient-single-label"
                    value={recipient}
                    onChange={handleRecipientChange}
                    input={<OutlinedInput label="Choose Recipient" />}
                  >
                    {RECIPIENT_OPTIONS.map((opt) => (
                      <MenuItem value={opt.value} key={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Select Vendor (conditional) */}
            {recipient === "SPECIFIC_VENDOR" && (
              <Box sx={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
                <Box sx={{ width: "400px" }}>
                  <Typography
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "24px",
                      color: "#10383D",
                      marginTop: "5px",
                    }}
                  >
                    Select Vendor
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "24px",
                      color: "#10383D",
                    }}
                  >
                    Choose the specific vendor for this feedback
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="vendor-select-label">Select Vendor</InputLabel>
                    <Select
                      labelId="vendor-select-label"
                      value={vendorId}
                      label="Select Vendor"
                      onChange={(e) => setVendorId(e.target.value)}
                    >
                      {vendorsList?.results?.results?.map((vendor: any) => (
                        <MenuItem key={vendor.vendor_id} value={vendor.vendor_id}>
                          {vendor.pseudo_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            {/* Feedback Title */}
            <Box sx={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
              <Box sx={{ width: "400px" }}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    color: "#10383D",
                    marginTop: "5px",
                  }}
                >
                  Feedback Title
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "#10383D",
                  }}
                >
                  Enter a title for this feedback
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Enter feedback title"
                  error={errors.feedback}
                  helperText={errors.feedback}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  size="small"
                />
              </Box>
            </Box>

            {/* Feedback Description */}
            <Box sx={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
              <Box sx={{ width: "400px" }}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    color: "#10383D",
                    marginTop: "5px",
                  }}
                >
                  Feedback Description
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "#10383D",
                  }}
                >
                  This would be visible on Expert Side.
                </Typography>
              </Box>

              <Box sx={{ flex: 1, '& .editor-container': { maxWidth: '100%', margin: 0 } }}>
                <RichTextEditor
                  key={feedbackId || "new"}
                  onChange={handleDescriptionChange}
                  initialContent={longDescription}
                />
              </Box>
            </Box>

            <LoadingButton
              onClick={handleSubmit}
              variant="contained"
              loading={feedbackId ? updateFeedbackStatus === STATUS.PENDING : createFeedbackStatus === STATUS.PENDING}
              loadingPosition="start"
              sx={{
                fontSize: "14px",
                textTransform: "none",
                color: "#FFFFFF",
                width: "100%",
                backgroundColor: "#3D85C6",
                borderRadius: "8px",
                mt: 4,
              }}
            >
              {feedbackRow?.id ? "Update Feedback" : "Save Feedback"}
            </LoadingButton>
          </Box>
        </Paper>
      </Box>
      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default CreateFeedbackPage;